/**
 * Checkbox style mappings. No styles in the component; all class maps live here.
 *
 * Native `accent-*` painting ignores border-radius — use appearance-none so
 * `shape="circle"` (rounded-full) actually renders as a circle.
 *
 * @internal Used by Checkbox atom only.
 */

import type { CSSProperties } from 'react';
import { cn } from '@/utils/cn';
import type { CheckboxShape, CheckboxSize } from './Checkbox.types';

const CHECK_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2.5 6.2 4.8 8.5 9.5 3.5' stroke='%23fff' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

const INDETERMINATE_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 6h6' stroke='%23fff' stroke-width='1.75' stroke-linecap='round'/%3E%3C/svg%3E\")";

const BASE_CLASSES =
  'appearance-none shrink-0 border border-light-lila bg-light ' +
  'bg-center bg-no-repeat ' +
  'checked:border-purple checked:bg-purple ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-1 ' +
  'transition-colors duration-200';

const SIZE_CLASSES: Record<CheckboxSize, string> = {
  sm: 'w-3.5 h-3.5 bg-[length:0.625rem_0.625rem]',
  md: 'w-4 h-4 bg-[length:0.75rem_0.75rem]',
  lg: 'w-5 h-5 bg-[length:0.875rem_0.875rem]',
};

const SHAPE_CLASSES: Record<CheckboxShape, string> = {
  square: 'rounded-[3px]',
  circle: 'rounded-full',
};

const DISABLED_CLASSES =
  'cursor-not-allowed opacity-50 checked:border-dark-invirtus-soft checked:bg-dark-invirtus-soft';

const ENABLED_CLASSES = 'cursor-pointer hover:border-purple/55';

export function getCheckboxClasses(
  size: CheckboxSize,
  disabled: boolean,
  className?: string,
  shape: CheckboxShape = 'square'
): string {
  return cn(
    BASE_CLASSES,
    SIZE_CLASSES[size],
    SHAPE_CLASSES[shape],
    disabled ? DISABLED_CLASSES : ENABLED_CLASSES,
    className
  );
}

export function getCheckboxGlyphStyle(options: {
  checked?: boolean;
  indeterminate?: boolean;
}): CSSProperties | undefined {
  if (options.indeterminate) {
    return {
      backgroundImage: INDETERMINATE_SVG,
      backgroundColor: 'var(--color-purple)',
      borderColor: 'var(--color-purple)',
    };
  }
  if (options.checked) {
    return { backgroundImage: CHECK_SVG };
  }
  return undefined;
}
